/**
 * attachmentIntent — does a recommended message need the user to ATTACH a
 * document before it can succeed?
 *
 * Owner directive 2026-08-12: chips like "Translate this letter to Spanish"
 * are great capability demonstrations, but auto-sending them fires a turn
 * with no letter attached — the user never got the chance to supply the
 * document. When a chip REFERENCES a document the user must provide, the pick
 * should stage the text in the composer and open the attachment picker in the
 * same click, not send.
 *
 * Detection is deliberately conservative — demonstrative-referenced documents
 * only ("this letter", "these intake documents", "the attached EOBs"). Chips
 * that name an app source ("the newest documents in my Drive") pull their
 * document THROUGH the connection, and chips about already-uploaded material
 * ("my latest uploaded document") already have it — neither needs the picker.
 * A missed detection degrades to today's behavior (Prest0n itself asks for
 * the document), so false negatives are cheap and false positives annoying —
 * the regexes lean tight.
 */

import { detectRequiredIntegrations } from './capabilityGrounding';

/** Demonstrative + (up to two modifiers) + document noun, EN + ES. */
const DOC_REFERENCE_PATTERNS: ReadonlyArray<RegExp> = [
  /\b(?:this|these|the)\s+(?:[a-z][a-z-]*\s+){0,2}?(?:letter|letters|document|documents|doc|docs|file|files|contract|contracts|agreement|agreements|lease|leases|form|forms|paperwork|intake|memo|memos|correspondence|record|records|pdf|pdfs|invoice|invoices|statement|statements|eob|eobs|transcript|transcripts)\b/i,
  /\b(?:the|this|these)\s+attached\b/i,
  /\b(?:este|esta|estos|estas|el|la|los|las)\s+(?:[a-z][a-záéíóúñ-]*\s+){0,2}?(?:documento|documentos|carta|cartas|archivo|archivos|contrato|contratos|formulario|formularios|expediente|expedientes|factura|facturas)\b/i,
];

/** References to material the account already holds — no picker needed. */
const ALREADY_AVAILABLE = /\buploaded\b|\bsubid[oa]s?\b/i;

/**
 * True when sending this chip verbatim would strand the user without the
 * document it operates on — i.e. it references a document demonstratively,
 * names no connected-app source to pull it from, and is not about material
 * already uploaded to the account.
 */
export function chipNeedsAttachment(text: string): boolean {
  const value = typeof text === 'string' ? text : '';
  if (!value.trim()) return false;
  if (ALREADY_AVAILABLE.test(value)) return false;
  if (!DOC_REFERENCE_PATTERNS.some((re) => re.test(value))) return false;
  // An app source ("in my Drive", "from my inbox") supplies the document
  // through the connection — the chip is capability-grounded separately.
  return detectRequiredIntegrations(value).length === 0;
}
